import { useCallback, useEffect, useMemo, useState } from "react";
import { X, Star, User, Bot } from "lucide-react";
import type { KeepsakedMessage } from "../hooks/useKeepsakes";
import { resolveActiveCompanion, type CompanionDescriptor } from "../brain/persona";
import {
  relativeTime,
  stripMarkdown,
  truncate,
  contentTypeIcon,
  CopyButton,
} from "./KeepsakesList";

interface KeepsakesGalleryProps {
  open: boolean;
  onClose: () => void;
  /** Cross-session, cross-identity keepsakes (GET /api/keepsakes, no
   *  ?companion= filter — see transcript.ts getAllKeepsakesAcrossIdentities). */
  keepsakes: KeepsakedMessage[];
  companions?: CompanionDescriptor[] | null;
  onSelect: (messageId: string, sessionId: string) => void;
}

function GalleryCard({
  k,
  companionLabel,
  onSelect,
}: {
  k: KeepsakedMessage;
  companionLabel: string;
  onSelect: (messageId: string, sessionId: string) => void;
}) {
  const preview = truncate(stripMarkdown(k.content), 160);
  const sessionLabel = k.session_label ?? "Untitled";

  return (
    <div className="keepsake-card" role="article">
      <CopyButton content={k.content} />
      <button
        className="keepsake-card__body"
        onClick={() => onSelect(k.id, k.session_id)}
        aria-label={`Go to keepsake: ${preview}`}
      >
        <div className="keepsake-card__header">
          <span className="keepsake-card__type-icon">{contentTypeIcon(k.content)}</span>
          <span className="keepsake-card__role">
            {k.role === "user" ? <User size={11} /> : <Bot size={11} />}
          </span>
          <span className="session-item-companion-badge keepsakes-gallery-card__companion">
            {companionLabel}
          </span>
        </div>
        <p className="keepsake-card__preview">{preview}</p>
        <div className="keepsake-card__meta">
          <span className="keepsake-card__session-badge">{sessionLabel}</span>
          <span className="keepsake-card__time">{relativeTime(k.created_at)}</span>
        </div>
      </button>
    </div>
  );
}

/**
 * Full cross-session, cross-identity keepsakes browser — the surface that
 * makes every one of Falco's saved moments reachable, not just the ~50 most
 * recent from whichever companion happened to be the REST default.
 *
 * First cut: functional and consistent with the app's existing overlay/panel
 * pattern (mirrors SettingsPanel's shell), reusing the sidebar's keepsake
 * card styling. Falco to review the visual design separately.
 */
export function KeepsakesGallery({ open, onClose, keepsakes, companions, onSelect }: KeepsakesGalleryProps) {
  const [filter, setFilter] = useState<string>("all");

  // Escape key closes the gallery. AppShell's own global keydown handler also
  // checks keepsakesGalleryOpen and closes it — both firing on the same
  // Escape press is idempotent (setState to the same "closed" value twice),
  // matching the existing SettingsPanel convention in this codebase.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset to "all" every time the gallery opens fresh — prevents a filter
  // left on "rainer" from silently hiding rook keepsakes the next time it's
  // opened for an unrelated reason.
  useEffect(() => {
    if (open) setFilter("all");
  }, [open]);

  const identities = useMemo(
    () => Array.from(new Set(keepsakes.map((k) => k.session_identity))).sort(),
    [keepsakes],
  );

  const filtered = filter === "all" ? keepsakes : keepsakes.filter((k) => k.session_identity === filter);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div className="keepsakes-gallery-overlay" onClick={handleOverlayClick}>
      <div className="keepsakes-gallery-panel open">
        <div className="settings-header">
          <span className="display">Keepsakes</span>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close keepsakes gallery">
            <X size={18} />
          </button>
        </div>

        <div className="keepsakes-gallery-filters" role="tablist" aria-label="Filter keepsakes by companion">
          <button
            role="tab"
            aria-selected={filter === "all"}
            className={`keepsakes-gallery-filter${filter === "all" ? " active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All
            <span className="keepsakes-count">{keepsakes.length}</span>
          </button>
          {identities.map((id) => {
            const label = resolveActiveCompanion(companions, id).displayName;
            const count = keepsakes.filter((k) => k.session_identity === id).length;
            return (
              <button
                key={id}
                role="tab"
                aria-selected={filter === id}
                className={`keepsakes-gallery-filter${filter === id ? " active" : ""}`}
                onClick={() => setFilter(id)}
              >
                {label}
                <span className="keepsakes-count">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="keepsakes-gallery-scroll">
          {filtered.length === 0 ? (
            <div className="keepsakes-empty">
              <Star size={12} />
              <span>No keepsakes here yet</span>
            </div>
          ) : (
            <div className="keepsakes-gallery-grid">
              {filtered.map((k) => (
                <GalleryCard
                  key={k.id}
                  k={k}
                  companionLabel={resolveActiveCompanion(companions, k.session_identity).displayName}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
